import { useState, useEffect } from 'react';
import { supabase } from '../../../lib/supabase';

interface Gateway {
  id: string;
  name: string;
  slug: string;
  public_key: string | null;
  secret_key: string | null;
  webhook_secret: string | null;
  webhook_url: string | null;
  environment: 'sandbox' | 'production';
  is_active: boolean;
  is_default: boolean;
  currency: string;
  supports_recurring: boolean;
  checkout_type: 'transparent' | 'link';
  supports_installments: boolean;
  instructions: string | null;
  test_payer_email: string | null;
}

const GATEWAYS_META: Record<string, { label: string; icon: string; accent: string; description: string }> = {
  stripe: { label: 'Stripe', icon: 'ri-stripe-line', accent: '#6772e5', description: 'Gateway global com suporte a cartão, PIX e recorrência' },
  mercadopago: { label: 'Mercado Pago', icon: 'ri-secure-payment-line', accent: '#009ee3', description: 'Principal gateway do Brasil com PIX, boleto e parcelamento' },
  asaas: { label: 'Asaas', icon: 'ri-bank-line', accent: '#00c48c', description: 'Gateway brasileiro especializado em cobranças recorrentes' },
  pagarme: { label: 'Pagar.me', icon: 'ri-money-dollar-circle-line', accent: '#65c178', description: 'Gateway completo com suporte a múltiplos métodos de pagamento' },
};

const AdminGatewaysPage = () => {
  const [gateways, setGateways] = useState<Gateway[]>([]);
  const [editing, setEditing] = useState<Gateway | null>(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [showSecrets, setShowSecrets] = useState<Record<string, boolean>>({});

  useEffect(() => {
    supabase.from('payment_gateways').select('*').order('created_at').then(({ data }) => setGateways((data as Gateway[]) || []));
  }, []);

  const handleSave = async () => {
    if (!editing) return;
    setSaving(true);
    setSaveError('');
    const { error } = await supabase.from('payment_gateways').update({
      public_key: editing.public_key,
      secret_key: editing.secret_key,
      webhook_secret: editing.webhook_secret,
      webhook_url: editing.webhook_url,
      environment: editing.environment,
      is_active: editing.is_active,
      is_default: editing.is_default,
      currency: editing.currency,
      supports_recurring: editing.supports_recurring,
      checkout_type: editing.checkout_type,
      supports_installments: editing.supports_installments,
      instructions: editing.instructions,
      test_payer_email: editing.test_payer_email,
    }).eq('id', editing.id);

    if (error) {
      setSaveError('Erro ao salvar: ' + error.message);
      setSaving(false);
      return;
    }

    if (editing.is_default) {
      await supabase.from('payment_gateways').update({ is_default: false }).neq('id', editing.id);
      setGateways((prev) => prev.map((g) => ({ ...g, is_default: g.id === editing.id })));
    }
    setGateways((prev) => prev.map((g) => (g.id === editing.id ? editing : g)));
    setSaving(false);
    setSaved(true);
    setTimeout(() => { setSaved(false); setEditing(null); }, 1500);
  };

  const toggleSecret = (field: string) => setShowSecrets((prev) => ({ ...prev, [field]: !prev[field] }));
  const maskValue = (v: string | null) => v ? '••••' + v.slice(-6) : 'Não configurado';

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-[24px] font-700 text-white">Gateways de Pagamento</h1>
        <p className="text-white/40 text-[14px] mt-1">Configure as integrações com processadores de pagamento</p>
      </div>

      {/* Status summary cards */}
      <div className="grid grid-cols-4 gap-4 mb-6">
        {gateways.map((gw) => {
          const meta = GATEWAYS_META[gw.slug] || { label: gw.name, icon: 'ri-secure-payment-line', accent: '#888', description: '' };
          const hasKeys = !!(gw.public_key && gw.secret_key);
          const sandboxMissingEmail = gw.environment === 'sandbox' && !gw.test_payer_email && gw.slug === 'mercadopago';
          return (
            <div
              key={gw.id}
              className={`border rounded-2xl p-4 transition-all cursor-pointer ${editing?.id === gw.id ? 'border-violet-500/50' : 'border-white/10 hover:border-white/20'}`}
              style={{ background: `${meta.accent}10` }}
              onClick={() => setEditing(editing?.id === gw.id ? null : { ...gw })}
            >
              <div className="flex items-center justify-between mb-3">
                <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-white/10">
                  <i className={`${meta.icon} text-[20px]`} style={{ color: meta.accent }} />
                </div>
                <div className="flex flex-col items-end gap-1">
                  <span className={`text-[10px] font-700 px-2 py-0.5 rounded-full ${gw.is_active ? 'bg-emerald-500/20 text-emerald-400' : 'bg-white/10 text-white/40'}`}>
                    {gw.is_active ? 'Ativo' : 'Inativo'}
                  </span>
                  {gw.is_default && <span className="text-[10px] font-700 px-2 py-0.5 rounded-full bg-violet-500/20 text-violet-400">Padrão</span>}
                  {sandboxMissingEmail && <span className="text-[10px] font-700 px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-400">Config. pendente</span>}
                </div>
              </div>
              <h3 className="text-[15px] font-700 text-white mb-0.5">{meta.label}</h3>
              <p className="text-[11px] text-white/40 leading-tight">{meta.description}</p>
              <div className="mt-3 flex gap-3 text-[11px] flex-wrap">
                <span className={gw.environment === 'production' ? 'text-emerald-400' : 'text-yellow-400'}>
                  {gw.environment === 'production' ? 'Produção' : 'Sandbox'}
                </span>
                <span className={hasKeys ? 'text-emerald-400' : 'text-white/30'}>
                  {hasKeys ? 'Credenciais OK' : 'Sem credenciais'}
                </span>
              </div>
            </div>
          );
        })}
      </div>

      {/* Edit panel */}
      {editing && (
        <div className="bg-white/5 border border-white/10 rounded-2xl overflow-hidden">
          <div className="flex items-center justify-between px-5 py-4 border-b border-white/10">
            <h2 className="text-[15px] font-700 text-white">
              Configurar: {GATEWAYS_META[editing.slug]?.label || editing.name}
            </h2>
            <button onClick={() => setEditing(null)} className="w-8 h-8 flex items-center justify-center bg-white/5 hover:bg-white/10 rounded-lg cursor-pointer transition-colors">
              <i className="ri-close-line text-white/60" />
            </button>
          </div>

          <div className="p-5">
            {/* MP Sandbox setup guide */}
            {editing.slug === 'mercadopago' && editing.environment === 'sandbox' && (
              <div className="mb-5 bg-yellow-500/10 border border-yellow-500/20 rounded-xl p-4">
                <div className="flex items-start gap-3">
                  <i className="ri-flask-line text-yellow-400 text-[16px] flex-shrink-0 mt-0.5" />
                  <div className="w-full">
                    <p className="text-[13px] font-700 text-yellow-400 mb-2">Como configurar o sandbox corretamente</p>
                    <p className="text-[12px] text-white/50 leading-relaxed mb-3">
                      O Mercado Pago sandbox exige uma configuração específica — usar credenciais e e-mail da conta errada causa o erro <code className="bg-white/10 px-1 rounded text-yellow-300">&quot;Unauthorized use of live credentials&quot;</code>.
                    </p>

                    <div className="space-y-3">
                      {/* Step 1 */}
                      <div className="bg-white/5 rounded-lg p-3">
                        <p className="text-[11px] font-700 text-yellow-300 mb-1.5">Passo 1 — Criar contas de teste no MP</p>
                        <ol className="text-[11px] text-white/50 space-y-1 list-decimal list-inside">
                          <li>Acesse <strong className="text-white/70">mercadopago.com.br/developers</strong></li>
                          <li>Vá em sua aplicação → aba <strong className="text-white/70">&quot;Contas de teste&quot;</strong></li>
                          <li>Crie uma conta tipo <strong className="text-yellow-300">Vendedor</strong> (seller)</li>
                          <li>Crie uma conta tipo <strong className="text-yellow-300">Comprador</strong> (buyer)</li>
                        </ol>
                      </div>

                      {/* Step 2 */}
                      <div className="bg-white/5 rounded-lg p-3">
                        <p className="text-[11px] font-700 text-yellow-300 mb-1.5">Passo 2 — Usar credenciais do Vendedor de teste</p>
                        <ol className="text-[11px] text-white/50 space-y-1 list-decimal list-inside">
                          <li>Copie o <strong className="text-white/70">Public Key</strong> e <strong className="text-white/70">Access Token</strong> da conta <strong className="text-yellow-300">Vendedor de teste</strong></li>
                          <li>Cole nos campos &quot;Public Key&quot; e &quot;Access Token&quot; abaixo</li>
                        </ol>
                        <div className="mt-2 bg-yellow-500/5 border border-yellow-500/10 rounded p-2">
                          <p className="text-[10px] text-yellow-400/80">
                            <strong>Atenção:</strong> Use as credenciais da <em>conta Vendedor de teste</em>, não da sua conta principal. As credenciais de teste podem ter o mesmo formato <code className="bg-white/10 px-1 rounded">APP_USR-</code> das de produção.
                          </p>
                        </div>
                      </div>

                      {/* Step 3 */}
                      <div className="bg-white/5 rounded-lg p-3">
                        <p className="text-[11px] font-700 text-yellow-300 mb-1.5">Passo 3 — Configurar e-mail do Comprador de teste (obrigatório)</p>
                        <p className="text-[11px] text-white/50 mb-2">
                          O campo <strong className="text-white/70">&quot;E-mail do Comprador de Teste&quot;</strong> abaixo deve ter o e-mail da conta <strong className="text-yellow-300">Comprador de teste</strong> criada no Passo 1. Esse e-mail é usado automaticamente como pagador em todas as transações sandbox.
                        </p>
                        <div className="bg-yellow-500/5 border border-yellow-500/10 rounded p-2">
                          <p className="text-[10px] text-yellow-400/80">
                            <strong>Por que?</strong> O MP sandbox rejeita pagamentos se o pagador usar o mesmo e-mail da conta do desenvolvedor (auto-compra). O e-mail do Comprador de teste é diferente e resolve isso.
                          </p>
                        </div>
                      </div>

                      {/* Test card info */}
                      <div className="bg-white/5 rounded-lg p-3">
                        <p className="text-[11px] font-700 text-yellow-300 mb-1.5">Cartão de teste para pagamento</p>
                        <div className="grid grid-cols-2 gap-2 text-[11px] text-white/50">
                          <div><span className="text-white/30">Nº:</span> <strong className="text-white/60">4509 9535 6623 3704</strong></div>
                          <div><span className="text-white/30">CVV:</span> <strong className="text-white/60">123</strong></div>
                          <div><span className="text-white/30">Nome titular:</span> <strong className="text-yellow-300">APRO</strong> (força aprovação)</div>
                          <div><span className="text-white/30">CPF:</span> <strong className="text-white/60">12345678909</strong></div>
                        </div>
                        <p className="text-[10px] text-white/30 mt-2">Use qualquer data de validade futura.</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div className="grid grid-cols-2 gap-5">
              {/* Left column: Credentials */}
              <div className="space-y-4">
                <p className="text-[11px] font-700 text-white/30 uppercase tracking-wider">Credenciais</p>

                {[
                  { label: 'Public Key', key: 'public_key' as const, placeholder: 'APP_USR-...', secret: false },
                  { label: 'Access Token (Chave Secreta)', key: 'secret_key' as const, placeholder: 'APP_USR-...', secret: true },
                  { label: 'Webhook Secret', key: 'webhook_secret' as const, placeholder: 'Opcional', secret: true },
                  { label: 'Webhook URL', key: 'webhook_url' as const, placeholder: 'https://...', secret: false },
                ].map((f) => (
                  <div key={f.key}>
                    <label className="text-[11px] text-white/40 uppercase tracking-wider font-600 mb-1.5 block">{f.label}</label>
                    <div className="relative">
                      <input
                        type={f.secret && !showSecrets[f.key] ? 'password' : 'text'}
                        value={editing[f.key] || ''}
                        onChange={(e) => setEditing({ ...editing, [f.key]: e.target.value })}
                        placeholder={f.placeholder}
                        className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2.5 text-white text-[13px] outline-none focus:border-violet-500 placeholder:text-white/20 pr-10"
                      />
                      {f.secret && (
                        <button onClick={() => toggleSecret(f.key)} className="absolute right-3 top-1/2 -translate-y-1/2 cursor-pointer" type="button">
                          <i className={`${showSecrets[f.key] ? 'ri-eye-off-line' : 'ri-eye-line'} text-white/30 text-[14px]`} />
                        </button>
                      )}
                    </div>
                  </div>
                ))}

                {/* Test payer email — shown only for MP in sandbox */}
                {editing.slug === 'mercadopago' && editing.environment === 'sandbox' && (
                  <div>
                    <label className="text-[11px] text-white/40 uppercase tracking-wider font-600 mb-1.5 block flex items-center gap-1.5">
                      E-mail do Comprador de Teste
                      <span className="text-[9px] font-700 px-1.5 py-0.5 bg-red-500/20 text-red-400 rounded-full normal-case">Obrigatório para sandbox</span>
                    </label>
                    <input
                      type="email"
                      value={editing.test_payer_email || ''}
                      onChange={(e) => setEditing({ ...editing, test_payer_email: e.target.value })}
                      placeholder="email-da-conta-comprador@testuser.com"
                      className={`w-full bg-white/5 border rounded-xl px-3 py-2.5 text-white text-[13px] outline-none focus:border-violet-500 placeholder:text-white/20 ${!editing.test_payer_email ? 'border-amber-500/40' : 'border-white/10'}`}
                    />
                    <p className="text-[10px] text-white/30 mt-1.5">
                      E-mail da conta &quot;Comprador&quot; criada na aba &quot;Contas de teste&quot; do painel MP. Resolve o erro &quot;Unauthorized use of live credentials&quot;.
                    </p>
                  </div>
                )}

                <div>
                  <label className="text-[11px] text-white/40 uppercase tracking-wider font-600 mb-1.5 block">Moeda</label>
                  <select
                    value={editing.currency || 'BRL'}
                    onChange={(e) => setEditing({ ...editing, currency: e.target.value })}
                    className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2.5 text-white text-[13px] outline-none focus:border-violet-500 cursor-pointer"
                  >
                    <option value="BRL">BRL — Real Brasileiro</option>
                    <option value="USD">USD — Dólar Americano</option>
                    <option value="EUR">EUR — Euro</option>
                  </select>
                </div>
              </div>

              {/* Right column: Settings */}
              <div className="space-y-4">
                <p className="text-[11px] font-700 text-white/30 uppercase tracking-wider">Configurações</p>

                <div>
                  <label className="text-[11px] text-white/40 uppercase tracking-wider font-600 mb-1.5 block">Ambiente</label>
                  <div className="flex gap-2">
                    {(['sandbox', 'production'] as const).map((env) => (
                      <button
                        key={env}
                        onClick={() => setEditing({ ...editing, environment: env })}
                        className={`flex-1 py-2.5 rounded-xl text-[12px] font-600 transition-colors cursor-pointer whitespace-nowrap ${editing.environment === env ? env === 'production' ? 'bg-emerald-600 text-white' : 'bg-yellow-600 text-white' : 'bg-white/5 text-white/40 hover:bg-white/10'}`}
                      >
                        {env === 'sandbox' ? 'Sandbox (Teste)' : 'Produção'}
                      </button>
                    ))}
                  </div>
                  <p className="text-[10px] text-white/30 mt-1.5">
                    {editing.environment === 'sandbox'
                      ? 'Use credenciais da conta Vendedor de teste (ver guia acima).'
                      : 'Use credenciais de produção da sua conta Mercado Pago.'}
                  </p>
                </div>

                <div>
                  <label className="text-[11px] text-white/40 uppercase tracking-wider font-600 mb-1.5 block">Tipo de Checkout</label>
                  <div className="flex gap-2">
                    {([['transparent', 'Transparente'], ['link', 'Link de Pagamento']] as const).map(([v, l]) => (
                      <button
                        key={v}
                        onClick={() => setEditing({ ...editing, checkout_type: v })}
                        className={`flex-1 py-2.5 rounded-xl text-[12px] font-600 transition-colors cursor-pointer whitespace-nowrap ${editing.checkout_type === v ? 'bg-violet-600 text-white' : 'bg-white/5 text-white/40 hover:bg-white/10'}`}
                      >
                        {l}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="space-y-3 pt-1">
                  {[
                    { label: 'Gateway ativo', key: 'is_active' as const },
                    { label: 'Gateway padrão do sistema', key: 'is_default' as const },
                    { label: 'Recorrência habilitada', key: 'supports_recurring' as const },
                    { label: 'Parcelamento habilitado', key: 'supports_installments' as const },
                  ].map((toggle) => (
                    <div key={toggle.key} className="flex items-center justify-between py-2 border-b border-white/5">
                      <span className="text-[13px] text-white/60">{toggle.label}</span>
                      <button
                        onClick={() => setEditing({ ...editing, [toggle.key]: !editing[toggle.key] })}
                        className={`w-11 h-6 rounded-full transition-colors cursor-pointer relative flex-shrink-0 overflow-hidden ${editing[toggle.key] ? 'bg-violet-600' : 'bg-white/10'}`}
                      >
                        <span className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-all ${editing[toggle.key] ? 'left-6' : 'left-1'}`} />
                      </button>
                    </div>
                  ))}
                </div>

                <div>
                  <label className="text-[11px] text-white/40 uppercase tracking-wider font-600 mb-1.5 block">Instruções / Observações</label>
                  <textarea
                    value={editing.instructions || ''}
                    onChange={(e) => setEditing({ ...editing, instructions: e.target.value })}
                    rows={3}
                    maxLength={500}
                    className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2.5 text-white text-[13px] outline-none focus:border-violet-500 placeholder:text-white/20 resize-none"
                    placeholder="Notas internas..."
                  />
                </div>
              </div>
            </div>

            {saveError && (
              <div className="mt-4 bg-red-500/10 border border-red-500/20 rounded-xl p-3 flex items-center gap-2">
                <i className="ri-error-warning-line text-red-400 text-[14px]" />
                <p className="text-[12px] text-red-400">{saveError}</p>
              </div>
            )}

            <div className="flex justify-end gap-3 mt-5 pt-5 border-t border-white/10">
              <button onClick={() => setEditing(null)} className="px-4 py-2.5 bg-white/5 hover:bg-white/10 text-white/60 text-[13px] font-600 rounded-xl transition-colors cursor-pointer whitespace-nowrap">
                Cancelar
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="px-6 py-2.5 bg-violet-600 hover:bg-violet-700 disabled:opacity-60 text-white text-[13px] font-600 rounded-xl transition-colors cursor-pointer whitespace-nowrap"
              >
                {saving ? 'Salvando...' : saved ? '✓ Salvo!' : 'Salvar Configurações'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Preview cards when not editing */}
      {!editing && (
        <div className="grid grid-cols-2 gap-4">
          {gateways.map((gw) => {
            const meta = GATEWAYS_META[gw.slug] || { label: gw.name, icon: 'ri-secure-payment-line', accent: '#888' };
            const sandboxReady = gw.environment !== 'sandbox' || gw.slug !== 'mercadopago' || !!gw.test_payer_email;
            return (
              <div key={gw.id} className="bg-white/5 border border-white/10 rounded-2xl p-4 space-y-3">
                <div className="flex items-center gap-3 pb-3 border-b border-white/5">
                  <i className={`${meta.icon} text-[16px]`} style={{ color: meta.accent }} />
                  <span className="text-[14px] font-700 text-white">{meta.label}</span>
                  <span className={`ml-auto text-[10px] font-700 px-2 py-0.5 rounded-full ${gw.environment === 'sandbox' ? 'bg-yellow-500/20 text-yellow-400' : 'bg-emerald-500/20 text-emerald-400'}`}>
                    {gw.environment === 'sandbox' ? 'Sandbox' : 'Produção'}
                  </span>
                </div>
                {[
                  { label: 'Public Key', value: maskValue(gw.public_key) },
                  { label: 'Access Token', value: gw.secret_key ? '••••' + gw.secret_key.slice(-6) : 'Não configurado' },
                  { label: 'Webhook URL', value: gw.webhook_url || 'Não configurado' },
                  { label: 'Checkout', value: gw.checkout_type === 'transparent' ? 'Transparente' : 'Link' },
                  { label: 'Status', value: gw.is_active ? 'Ativo' : 'Inativo' },
                ].map((row) => (
                  <div key={row.label} className="flex justify-between items-center text-[12px]">
                    <span className="text-white/30">{row.label}</span>
                    <span className="text-white/60 text-right max-w-[60%] truncate">{row.value}</span>
                  </div>
                ))}
                {!sandboxReady && (
                  <div className="flex items-center gap-1.5 pt-1 border-t border-white/5">
                    <i className="ri-error-warning-line text-amber-400 text-[12px]" />
                    <span className="text-[11px] text-amber-400">E-mail comprador de teste não configurado</span>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default AdminGatewaysPage;
